import { isNil } from "lodash";
import { type CrownEnv } from "../../components/crown-data-import/env/env-config";
import {
  BenchIntegrationType,
  BenchStepOutcome,
  BenchStepState,
  type BenchRunReportFieldsFragment,
  type OrderStatus,
  type OutboundBenchCompanyUsersQuery,
  type OutboundBenchDriversQuery,
  type OutboundBenchOrdersQuery,
  type OutboundBenchPartnershipsQuery,
  type PlanOutboundBenchRunInput,
} from "../../generated/graphql";
import { skippedStepSelectors, stepLabel } from "./bench-steps";

/** The text an operator types to unlock a production run. */
export const PRODUCTION_CONFIRMATION_PHRASE = "prod";

export type BenchPartnershipOption =
  OutboundBenchPartnershipsQuery["outboundBenchPartnerships"][number];

export type BenchOrderOption =
  OutboundBenchOrdersQuery["outboundBenchOrders"][number];

export type BenchCompanyUserOption =
  OutboundBenchCompanyUsersQuery["outboundBenchCompanyUsers"][number];

export type BenchDriverOption =
  OutboundBenchDriversQuery["outboundBenchDrivers"][number];

/** The backend's cap on the orders one run may name (`MAX_BENCH_ORDERS`). */
export const MAX_PICKED_ORDERS = 50;

/** Whether a Scenario run takes the picked orders or the partnership's newest. */
export type BenchOrderSelectionMode = "picked" | "newest";

/** `IN_PROGRESS` reads as "In progress". */
export const orderStatusLabel = (status: OrderStatus): string => {
  const words = status.toLowerCase().replaceAll("_", " ");
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

/** The order's created date as YYYY-MM-DD, in UTC so every operator reads the same day. */
export const formatOrderCreatedDate = (createdAt: unknown): string => {
  if (typeof createdAt !== "string") {
    return "";
  }
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

/** The picker's second line: HAWB, status and created date. */
export const orderOptionDetails = (option: BenchOrderOption): string =>
  [
    `HAWB ${option.hawb ?? "none"}`,
    orderStatusLabel(option.status),
    `created ${formatOrderCreatedDate(option.createdAt)}`,
  ].join(" · ");

export const orderOptionLabel = (option: BenchOrderOption): string =>
  `${option.name} · ${orderOptionDetails(option)}`;

/**
 * The picker's options: the current selection first, then the search
 * results without repeats. Keeping the selection in the list stops MUI from
 * rejecting a picked order that a new search no longer returns.
 */
export const mergeSelectedOrderOptions = (
  selected: readonly BenchOrderOption[],
  results: readonly BenchOrderOption[],
): BenchOrderOption[] => {
  const selectedUuids = new Set(selected.map((option) => option.uuid));
  return [
    ...selected,
    ...results.filter((option) => !selectedUuids.has(option.uuid)),
  ];
};

/**
 * The picked orders after a "Create test orders" call: the new orders join
 * the end without repeats, and the list stays within {@link MAX_PICKED_ORDERS}
 * by keeping the picked orders first.
 */
export const addCreatedOrders = (
  picked: readonly BenchOrderOption[],
  created: readonly BenchOrderOption[],
): BenchOrderOption[] =>
  mergeSelectedOrderOptions(picked, created).slice(0, MAX_PICKED_ORDERS);

/** The user's name, with the email beside it when the two differ. */
export const companyUserLabel = (option: BenchCompanyUserOption): string =>
  isNil(option.email) || option.email === option.displayName
    ? option.displayName
    : `${option.displayName} (${option.email})`;

type RunSelection = {
  partnership: BenchPartnershipOption | null;
  mode: BenchOrderSelectionMode;
  pickedOrders: readonly BenchOrderOption[];
  orderStatuses: readonly OrderStatus[];
  limit: string;
  companyActor: BenchCompanyUserOption | null;
  /** The driver ROUTE_STOP assigns the bench route to. */
  testDriver?: BenchDriverOption | null;
  /** Step keys the operator switched off for this run. */
  skippedStepKeys?: ReadonlySet<string>;
  /** Whether Accept tender sends the 990 ACCEPTED for an order that arrived live. */
  send990ForLiveOrders?: boolean;
};

/**
 * The Scenario input for the page's selection, or null while it names no
 * orders. Picked orders go as `orderUuids`. "Use the newest orders" sends the
 * partnership's company and contact, the status filter and the limit, and
 * the backend picks the orders.
 */
export const buildRunInput = ({
  partnership,
  mode,
  pickedOrders,
  orderStatuses,
  limit,
  companyActor,
  testDriver = null,
  skippedStepKeys,
  send990ForLiveOrders = false,
}: RunSelection): PlanOutboundBenchRunInput | null => {
  if (partnership === null) {
    return null;
  }
  if (mode === "picked" && pickedOrders.length === 0) {
    return null;
  }
  const parsedLimit = Number.parseInt(limit, 10);
  return {
    integrationType: BenchIntegrationType.Tenedi,
    selection:
      mode === "picked"
        ? { orderUuids: pickedOrders.map((order) => order.uuid) }
        : {
            companyUuid: partnership.companyUuid,
            contactUuid: partnership.contactUuid,
            orderStatuses:
              orderStatuses.length > 0 ? [...orderStatuses] : undefined,
            limit: Number.isFinite(parsedLimit) ? parsedLimit : undefined,
          },
    companyActors:
      companyActor === null
        ? undefined
        : [
            {
              companyUuid: partnership.companyUuid,
              userUuid: companyActor.uuid,
            },
          ],
    skippedSteps:
      skippedStepKeys === undefined || skippedStepKeys.size === 0
        ? undefined
        : skippedStepSelectors(skippedStepKeys),
    driverUuid: testDriver?.uuid,
    send990ForLiveOrders: send990ForLiveOrders ? true : undefined,
  };
};

/**
 * Whether the page may send. Outside production it always may. Against
 * production the operator must have typed {@link PRODUCTION_CONFIRMATION_PHRASE}
 * into the confirmation field; the backend refuses without the opt-in anyway.
 */
export const isProductionConfirmed = (env: CrownEnv, typed: string): boolean =>
  env !== "prod" || typed.trim() === PRODUCTION_CONFIRMATION_PHRASE;

/**
 * The `confirmProductionSend` value to send: `true` only for a confirmed
 * production run, and omitted everywhere else.
 */
export const confirmProductionSendFor = (
  env: CrownEnv,
  typed: string,
): boolean | undefined =>
  env === "prod" && isProductionConfirmed(env, typed) ? true : undefined;

const OUTCOMES_WITH_MORE_WORK: ReadonlySet<BenchStepOutcome> = new Set([
  BenchStepOutcome.Executed,
  BenchStepOutcome.Waiting,
]);

/**
 * Whether another round of the run has work: true while any order's last step
 * ran or waited on a manifest another order moved, and none failed. A failed
 * step stops the whole run, because the next round would retry it and could
 * repeat a partial write or a send. An order that finished, blocked or was
 * refused stays put, so calling again would only repeat that answer.
 */
export const shouldContinueRun = (
  report: BenchRunReportFieldsFragment,
): boolean =>
  report.orders.every(
    (order) => order.lastResult?.outcome !== BenchStepOutcome.Failed,
  ) &&
  report.orders.some((order) => {
    const outcome = order.lastResult?.outcome;
    return outcome !== undefined && OUTCOMES_WITH_MORE_WORK.has(outcome);
  });

/**
 * The input for every round after the first: the orders the first round
 * resolved, by uuid. "Use the newest orders" re-queries on each call, so an
 * order created mid-run would join the batch and an original one could drop
 * out unfinished.
 */
export const freezeRunSelection = (
  input: PlanOutboundBenchRunInput,
  firstRound: BenchRunReportFieldsFragment,
): PlanOutboundBenchRunInput => ({
  ...input,
  selection: { orderUuids: firstRound.orders.map((order) => order.orderUuid) },
});

/**
 * The `reportEdiSince` for every round after the first: the earliest step start
 * the first round reported. The server stamped it, so a browser clock that
 * runs ahead cannot hide that round's sends from later reports. Undefined when
 * no order reported a step start.
 */
export const runReportCursor = (
  firstRound: BenchRunReportFieldsFragment,
): string | undefined => {
  let earliest: { at: number; value: string } | undefined;
  for (const order of firstRound.orders) {
    const startedAt: unknown = order.lastResult?.startedAt;
    if (typeof startedAt === "string") {
      const at = Date.parse(startedAt);
      if (!Number.isNaN(at) && (earliest === undefined || at < earliest.at)) {
        earliest = { at, value: startedAt };
      }
    }
  }
  return earliest?.value;
};

/**
 * The steps the next round will run: each order's next PENDING step, with a
 * count when several orders share it. A blocked order runs nothing, so it is
 * left out. Null when no order has a step to run.
 */
export const nextStepSummary = (
  report: BenchRunReportFieldsFragment,
): string | null => {
  const counts = new Map<string, number>();
  for (const order of report.orders) {
    const step = order.nextStep;
    if (!isNil(step) && step.state === BenchStepState.Pending) {
      const label = stepLabel(step.kind, step.stopRole);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
  }
  if (counts.size === 0) {
    return null;
  }
  return [...counts]
    .map(([label, count]) => (count > 1 ? `${label} x${count}` : label))
    .join(", ");
};

/** Whole seconds from `now` until `at`, never below zero. */
export const secondsUntil = (at: number, now: number): number =>
  Math.max(0, Math.ceil((at - now) / 1000));

/**
 * The partnership a deep link names (`?partnership=<uuid>`), once the list has
 * loaded. `missing` means the link named a partnership the list does not
 * hold: it does not exist, or it does not send to Tenedi.
 */
export const resolveLinkedPartnership = (
  partnerships: readonly BenchPartnershipOption[],
  linkedUuid: string | null,
): { match: BenchPartnershipOption | null; missing: boolean } => {
  if (linkedUuid === null) {
    return { match: null, missing: false };
  }
  const match =
    partnerships.find((option) => option.uuid === linkedUuid) ?? null;
  return { match, missing: match === null };
};
